import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  Clock3,
  MessageSquare,
  Navigation,
  ExternalLink,
} from "lucide-react";

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

const WHATSAPP_NUMBER = "905372153407";
const PHONE_NUMBER = "+905372153407";

const WHATSAPP_MESSAGE = encodeURIComponent(
  "Merhaba, bilgi almak istiyorum."
);
const SMS_MESSAGE = encodeURIComponent(
  "Merhaba, bilgi almak istiyorum."
);

const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;
const SMS_URL = `sms:${PHONE_NUMBER}?body=${SMS_MESSAGE}`;
const PHONE_URL = `tel:${PHONE_NUMBER}`;

export default function Contact() {
  useEffect(() => {
    const url = "https://aysunseremet.com/iletisim";
    document.title =
      "İletişim — Uzm. Klinik Psikolog Aysun Şeremet | WhatsApp & Telefon";
    setMeta(
      "description",
      "Uzm. Klinik Psikolog Aysun Şeremet ile iletişime geçin. WhatsApp, telefon veya SMS yoluyla hızlı iletişim. Muratpaşa, Antalya klinik adresi."
    );
    let c = document.querySelector('link[rel="canonical"]');
    if (!c) {
      c = document.createElement("link");
      c.rel = "canonical";
      document.head.appendChild(c);
    }
    c.href = url;
  }, []);

  const track = (eventName) => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", eventName, {
        event_category: "contact",
        value: 1,
      });
    }
  };

  return (
    <section
      id="iletisim"
      className="relative bg-gradient-to-b from-white to-pink-50/30 px-6 py-24 lg:px-12"
    >
      {/* HERO */}
      <motion.div
        className="mx-auto max-w-4xl space-y-5 text-center"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-pink-600">
          İletişim
        </p>
        <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
          İletişime Geçin
        </h1>
        <div className="mx-auto h-px w-16 bg-pink-400" />
        <p className="mx-auto max-w-2xl text-base leading-relaxed text-gray-600 sm:text-lg">
          Sorularınız ve randevu talepleriniz için aşağıdaki kanallardan bize
          ulaşabilirsiniz. En hızlı yanıt için{" "}
          <span className="font-medium text-gray-900">WhatsApp</span>{" "}
          tercih edebilirsiniz.
        </p>
      </motion.div>

      {/* HIZLI İLETİŞİM BUTONLARI */}
      <div className="mx-auto mt-14 grid max-w-4xl grid-cols-1 gap-4 md:grid-cols-3">
        {/* WhatsApp */}
        <motion.a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("click_whatsapp_iletisim")}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.05 }}
          className="group flex flex-col items-center gap-3 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 px-6 py-8 text-center text-white shadow-lg shadow-emerald-500/20 transition hover:scale-[1.02] hover:shadow-xl hover:shadow-emerald-500/30"
        >
          <svg
            className="h-10 w-10"
            fill="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          <div>
            <p className="text-lg font-semibold">WhatsApp</p>
            <p className="mt-1 text-sm text-emerald-50">En hızlı yanıt</p>
          </div>
        </motion.a>

        {/* Telefon */}
        <motion.a
          href={PHONE_URL}
          onClick={() => track("click_phone_iletisim")}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="group flex flex-col items-center gap-3 rounded-2xl bg-white px-6 py-8 text-center text-gray-900 shadow-sm ring-1 ring-gray-200 transition hover:scale-[1.02] hover:ring-pink-300 hover:shadow-md"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-pink-100 text-pink-600">
            <Phone size={24} />
          </div>
          <div>
            <p className="text-lg font-semibold">Telefon</p>
            <p className="mt-1 text-sm text-gray-500">0537 215 34 07</p>
          </div>
        </motion.a>

        {/* SMS */}
        <motion.a
          href={SMS_URL}
          onClick={() => track("click_sms_iletisim")}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.15 }}
          className="group flex flex-col items-center gap-3 rounded-2xl bg-white px-6 py-8 text-center text-gray-900 shadow-sm ring-1 ring-gray-200 transition hover:scale-[1.02] hover:ring-pink-300 hover:shadow-md"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-pink-100 text-pink-600">
            <MessageSquare size={24} />
          </div>
          <div>
            <p className="text-lg font-semibold">SMS</p>
            <p className="mt-1 text-sm text-gray-500">Kısa mesaj gönderin</p>
          </div>
        </motion.a>
      </div>

      {/* İLETİŞİM BİLGİLERİ VE HARİTA */}
      <div className="mx-auto mt-16 grid max-w-5xl grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Bilgi Kartı */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl bg-white p-8 shadow-sm ring-1 ring-gray-100"
        >
          <h2 className="text-xl font-semibold text-gray-900">
            İletişim Bilgileri
          </h2>

          <div className="mt-6 space-y-5">
            <div className="flex items-start gap-4">
              <div className="rounded-2xl bg-pink-100 p-3 text-pink-600">
                <MapPin size={22} />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Adres</p>
                <p className="mt-1 text-gray-600">
                  Yeşilbahçe Mah. Metin Kasapoğlu Cad. Yakamoz Sit. A Blok, Zemin Kat, No: 56/2 Muratpaşa/ANTALYA
                  <br />
                  <span className="text-sm text-gray-500">
                    (Barut Otel yanı, Otobüs Durağı arkası)
                  </span>
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="rounded-2xl bg-pink-100 p-3 text-pink-600">
                <Phone size={22} />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Telefon / WhatsApp</p>
                <p className="mt-1 text-gray-600">+90 537 215 34 07</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="rounded-2xl bg-pink-100 p-3 text-pink-600">
                <Mail size={22} />
              </div>
              <div>
                <p className="font-semibold text-gray-900">E-posta</p>
                <a
                  href="mailto:info@aysunseremet.com"
                  onClick={() => track("click_email_iletisim")}
                  className="mt-1 inline-block text-gray-600 transition hover:text-pink-600"
                >
                  info@aysunseremet.com
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="rounded-2xl bg-pink-100 p-3 text-pink-600">
                <Clock3 size={22} />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Çalışma Saatleri</p>
                <p className="mt-1 text-gray-600">Pazartesi – Cuma: 09:00 – 19:00</p>
                <p className="text-gray-600">Cumartesi: 10:00 – 16:00</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Google Maps */}
        <motion.div
          initial={{ opacity: 0, x: 16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="overflow-hidden rounded-3xl bg-white p-3 shadow-sm ring-1 ring-gray-100"
        >
          <div className="relative overflow-hidden rounded-2xl bg-gray-100">
            <iframe
              title="Klinik Konumu"
              src="https://www.google.com/maps?q=36.866172790527344,30.72596549987793&z=17&output=embed"
              width="100%"
              height="360"
              style={{ border: 0, display: "block" }}
              allowFullScreen=""
              loading="eager"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          {/* Fallback butonlar — iframe yüklenemezse (Instagram in-app browser, adblocker vb.) */}
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <a
              href="https://www.google.com/maps/search/?api=1&query=36.866172790527344%2C30.72596549987793"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("click_map_view")}
              className="flex items-center justify-center gap-2 rounded-2xl bg-gray-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-gray-800 active:scale-[0.98]"
            >
              <MapPin size={16} />
              <span>Google Haritalar'da Aç</span>
            </a>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=36.866172790527344%2C30.72596549987793"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("click_map_directions")}
              className="flex items-center justify-center gap-2 rounded-2xl bg-pink-600 px-4 py-3 text-sm font-semibold text-white shadow-sm shadow-pink-500/20 transition hover:bg-pink-700 active:scale-[0.98]"
            >
              <Navigation size={16} />
              <span>Yol Tarifi Al</span>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
